import {
  Box,
  Table,
  Text,
  Badge,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import { FiX } from 'react-icons/fi'
import type { ContactTag } from '../../types/contactDetail'
import { TagPicker } from '../tags/TagPicker'

interface TagsSectionProps {
  tags: ContactTag[]
  canEdit: boolean
  onAssignTag: (tagId: string) => void
  onRemoveTag: (tagId: string) => void
  isAssigning: boolean
  isRemoving: boolean
}

const tagTypeColors: Record<string, string> = {
  Volunteer: 'green',
  Donor: 'purple',
  Activist: 'orange',
  Issue: 'blue',
  Member: 'teal',
  Leader: 'yellow',
  Supporter: 'cyan',
  Opposition: 'red',
  Undecided: 'gray',
  Custom: 'pink',
}

export function TagsSection({
  tags,
  canEdit,
  onAssignTag,
  onRemoveTag,
  isAssigning,
  isRemoving,
}: TagsSectionProps) {
  const assignedTagIds = tags.map(t => t.tagId)

  return (
    <Box>
      {canEdit && (
        <HStack justify="flex-end" mb={4}>
          <TagPicker
            excludeTagIds={assignedTagIds}
            onSelect={(tag) => onAssignTag(tag.id)}
            isDisabled={isAssigning}
          />
        </HStack>
      )}

      {tags.length === 0 ? (
        <Box bg="surface" p={8} borderRadius="lg" shadow="md" textAlign="center">
          <Text color="gray.500">No tags assigned to this contact.</Text>
        </Box>
      ) : (
        <Box bg="surface" borderRadius="lg" shadow="md" overflow="hidden">
          <Box overflowX="auto">
          <Table.Root size="sm">
            <Table.Header bg="gray.50">
              <Table.Row>
                <Table.ColumnHeader>Tag</Table.ColumnHeader>
                <Table.ColumnHeader>Type</Table.ColumnHeader>
                <Table.ColumnHeader>Assigned By</Table.ColumnHeader>
                <Table.ColumnHeader>Assigned Date</Table.ColumnHeader>
                {canEdit && <Table.ColumnHeader width="60px">Remove</Table.ColumnHeader>}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {tags.map((tag) => (
                <Table.Row key={tag.id} _hover={{ bg: 'gray.50' }}>
                  <Table.Cell fontWeight="medium">{tag.label}</Table.Cell>
                  <Table.Cell>
                    {tag.tagType ? (
                      <Badge
                        colorPalette={tagTypeColors[tag.tagType] ?? 'gray'}
                        fontSize="xs"
                      >
                        {tag.tagType}
                      </Badge>
                    ) : (
                      <Text fontSize="sm" color="gray.400">--</Text>
                    )}
                  </Table.Cell>
                  <Table.Cell fontSize="sm" color="gray.600">{tag.assignedByName}</Table.Cell>
                  <Table.Cell fontSize="sm" color="gray.500">
                    {new Date(tag.assignedDate).toLocaleDateString()}
                  </Table.Cell>
                  {canEdit && (
                    <Table.Cell>
                      <IconButton
                        aria-label="Remove tag"
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => onRemoveTag(tag.tagId)}
                        loading={isRemoving}><FiX /></IconButton>
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
          </Box>
        </Box>
      )}
    </Box>
  );
}
